import { useEffect, useMemo, useState, useCallback } from 'react';
import { DayPicker } from 'react-day-picker';
import * as Popover from '@radix-ui/react-popover';
import { differenceInMinutes, differenceInHours, differenceInDays, isToday, isYesterday, isSameDay } from 'date-fns';
import { FiCalendar, FiClock } from './react-icons/main';
import Button from './Button.jsx';
import TextWithEllipsis from '@/components/TextWithEllipsis';
import { getDateFromActionMap, getActionLabel, actionDaysMap } from '@/lib/dateRangeActions';

const formatChange = (date, showTimeRanges, timeFilter) => {
  if (Array.isArray(date)) {
    return date;
  } else if (typeof date === 'object' && date?.from && date?.to) {
    let from = date.from;
    let to = date.to;

    if (timeFilter && (timeFilter.startTime || timeFilter.endTime)) {
      if (from && timeFilter.startTime) {
        const [hours, minutes] = timeFilter.startTime.split(':').map(Number);
        from = new Date(from);
        from.setHours(hours, minutes, 0, 0);
      }
      if (to && timeFilter.endTime) {
        const [hours, minutes] = timeFilter.endTime.split(':').map(Number);
        to = new Date(to);
        to.setHours(hours, minutes, 59, 999);
      }
    }

    if (showTimeRanges) {
      return [from, to];
    }

    if (!from || !to || !(from instanceof Date) || !(to instanceof Date)) {
      return [from, to];
    }

    const timeDiff = to.getTime() - from.getTime();
    const isTimeBasedRange =
      timeDiff <= 24 * 60 * 60 * 1000 && // 24 hours or less
      (from.getHours() !== 0 ||
        from.getMinutes() !== 0 ||
        from.getSeconds() !== 0 || // from is not midnight
        to.getHours() !== 0 ||
        to.getMinutes() !== 0 ||
        to.getSeconds() !== 0); // to is not midnight

    if (isTimeBasedRange) {
      return [from, to];
    }

    const allDays = [];
    const currentDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());

    // eslint-disable-next-line no-unmodified-loop-condition
    while (currentDate <= to) {
      allDays.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDays;
  }

  return [date];
};

const formatDate = (date, includeYear = false) => {
  if (!date) return undefined;
  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    dateObj = new Date(year, month - 1, day);
  } else {
    dateObj = new Date(date);
  }
  return dateObj.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: includeYear || dateObj.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
};

const TimeInput = ({ label, value, onChange, placeholder }) => {
  return (
    <div className="time-input-group">
      <label className="time-label">{label}</label>
      <div className="time-input-wrapper">
        <FiClock className="time-icon" />
        <input type="time" className="time-input" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      </div>
    </div>
  );
};

const DatePicker = ({ className = '', mode = 'range', showActions = true, showIcon = true, onChange, initialValue = null, variant, size, container, showTimeRanges = false, emptyDateLabel = 'Pick a date', showClearButton = true, allowExpandLabel = false, alwaysShowYear = false, maxDays, allowAllTime = true }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [timeFilter, setTimeFilter] = useState({ startTime: '', endTime: '' });

  const emitChange = useCallback(
    ({ date, action = false, currentTimeFilter = timeFilter }) => {
      if (!action) {
        const formattedValue = !date ? undefined : formatChange(date, showTimeRanges, currentTimeFilter);

        if (formattedValue && showTimeRanges && (currentTimeFilter?.startTime || currentTimeFilter?.endTime)) {
          formattedValue._timeFilter = currentTimeFilter;
        }

        onChange?.(formattedValue);
        setValue(date);
        return;
      }

      const getDateFromAction = getDateFromActionMap();

      const actionDate = getDateFromAction[action]();
      const formattedValue = formatChange(actionDate, showTimeRanges, currentTimeFilter);

      if (Array.isArray(formattedValue)) {
        formattedValue._action = action;
        if (showTimeRanges && (currentTimeFilter?.startTime || currentTimeFilter?.endTime)) {
          formattedValue._timeFilter = currentTimeFilter;
        }
        onChange?.(formattedValue);
      } else if (formattedValue && typeof formattedValue === 'object') {
        onChange?.({ ...formattedValue, action, _timeFilter: currentTimeFilter });
      } else {
        onChange?.({ action, _timeFilter: currentTimeFilter });
      }

      if (mode === 'single' || mode === 'multiple') {
        setValue(formattedValue);
      } else {
        if (action === 'allTime') {
          setValue({ from: null, to: null, action });
        } else {
          setValue({ from: actionDate.from, to: actionDate.to, action });
        }
      }
    },
    [timeFilter, showTimeRanges, onChange, mode]
  );

  const onSelect = (date) => {
    if (maxDays && mode === 'range' && typeof date === 'object' && date?.from && date?.to) {
      const daysDifference = differenceInDays(date.to, date.from);
      if (daysDifference > maxDays - 1) {
        const clickedDate = value?.from && !isSameDay(date.from, value.from) ? date.from : date.to;
        // Start a new selection with the clicked date if out of range
        emitChange({ date: { from: clickedDate, to: clickedDate } });
        return;
      }
    }
    emitChange({ date });
  };

  const onAction = (action) => {
    emitChange({ action });
  };

  const onClear = () => {
    setValue(null);
    setTimeFilter({ startTime: '', endTime: '' });
    onChange?.(undefined);
    setOpen(false);
  };

  const handleTimeChange = (field, newTime) => {
    const newTimeFilter = { ...timeFilter, [field]: newTime };
    setTimeFilter(newTimeFilter);

    if (value?.action) {
      emitChange({ action: value.action, currentTimeFilter: newTimeFilter });
    } else if (value?.from || value?.to) {
      emitChange({ date: value, currentTimeFilter: newTimeFilter });
    }
  };

  const clearTimeFilter = () => {
    const clearedTimeFilter = { startTime: '', endTime: '' };
    setTimeFilter(clearedTimeFilter);

    if (value?.action) {
      emitChange({ action: value.action, currentTimeFilter: clearedTimeFilter });
    } else if (value?.from || value?.to) {
      emitChange({ date: value, currentTimeFilter: clearedTimeFilter });
    }
  };

  const hasTimeFilter = timeFilter.startTime || timeFilter.endTime;

  const isActionAllowed = (actionValue) => {
    if (!maxDays) return true;
    const actionDays = actionDaysMap[actionValue];
    return actionDays !== undefined && actionDays <= maxDays;
  };

  const actions = [
    ...(showTimeRanges
      ? [
          { label: 'Last 30 minutes', value: 'last30Minutes' },
          { label: 'Last 1 hour', value: 'last1Hour' },
          { label: 'Last 2 hours', value: 'last2Hours' },
          { label: 'Last 3 hours', value: 'last3Hours' },
          { label: 'Last 6 hours', value: 'last6Hours' },
          { label: 'Last 12 hours', value: 'last12Hours' }
        ]
      : []),
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    ...(mode === 'range'
      ? [
          { label: 'Last 24 hours', value: 'last24Hours' },
          { label: 'Last 2 days', value: 'last2Days' },
          { label: 'Last 3 days', value: 'last3Days' },
          { label: 'Last 7 days', value: 'last7Days' },
          { label: 'Last 30 days', value: 'last30Days' },
          { label: 'This week', value: 'thisWeek' },
          { label: 'This month', value: 'thisMonth' },
          { label: 'This year', value: 'thisYear' }
        ]
      : []),
    ...(allowAllTime ? [{ label: 'All-time', value: 'allTime' }] : [])
  ].filter((action) => isActionAllowed(action.value));

  const buttonLabel = useMemo(() => {
    if (!value) {
      return emptyDateLabel;
    }

    if (value?.length === 0) {
      return 'All time';
    }

    if (value?.action) {
      return getActionLabel(value.action);
    }

    if (mode === 'single' && !Array.isArray(value)) {
      return formatDate(value, alwaysShowYear);
    }

    if (Array.isArray(value) && value.length === 1) {
      return formatDate(value[0], alwaysShowYear);
    }

    if (mode === 'multiple') {
      return value.map((date) => formatDate(date, alwaysShowYear)).join(', ');
    }

    if (mode === 'range') {
      if (value?.from === value?.to) {
        return !value?.from ? 'All time' : formatDate(value?.from, alwaysShowYear);
      }

      if (showTimeRanges && value?.from && value?.to) {
        const diffMinutes = differenceInMinutes(value.to, value.from);
        const diffHours = differenceInHours(value.to, value.from);
        const diffDays = differenceInDays(value.to, value.from);

        if (diffDays < 1) {
          if (diffMinutes < 60) {
            return `Last ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
          } else if (diffHours < 13) {
            return `Last ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
          }
        }
      }

      if (value?.from && value?.to && isSameDay(value.from, value.to)) {
        if (isToday(value.from)) {
          return 'Today';
        } else if (isYesterday(value.to)) {
          return 'Yesterday';
        } else {
          return formatDate(value.from, alwaysShowYear);
        }
      }

      if (isYesterday(value?.from) && isToday(value?.to)) {
        return 'Last 24 hours';
      }

      if (value?.from?.getFullYear?.() !== value?.to?.getFullYear?.()) {
        return `${formatDate(value?.from, true)} - ${formatDate(value?.to, true)}`;
      }

      return `${formatDate(value?.from, alwaysShowYear)} - ${formatDate(value?.to, alwaysShowYear)}`;
    }
  }, [value, showTimeRanges, alwaysShowYear]);

  useEffect(() => {
    setValue(initialValue);
    if (initialValue?._timeFilter) {
      setTimeFilter(initialValue._timeFilter);
    }
  }, [initialValue]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={'date-picker-trigger'} asChild>
        <div>
          <Button className={`${variant || 'filled pill'} ${className}`} size={size} icon={showIcon ? FiCalendar : undefined} onClick={() => setOpen(true)}>
            <TextWithEllipsis className={`date-picker-label ${allowExpandLabel ? 'allow-expand' : ''}`}>{buttonLabel}</TextWithEllipsis>
          </Button>
        </div>
      </Popover.Trigger>
      <Popover.Portal container={container}>
        <Popover.Content className={'comp-date-picker'} sideOffset={4} collisionPadding={8} align={'end'}>
          {showActions && (
            <div>
              <div className={'base actions'}>
                {actions.map((action) => (
                  <button key={action.value} className={'action'} onClick={() => onAction(action.value)}>
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className={'base picker'}>
              <DayPicker
                mode={mode}
                showOutsideDays
                selected={value}
                onSelect={onSelect}
                disabled={(date) => {
                  // Disable dates that would exceed maxDays when selected
                  if (maxDays && mode === 'range' && value?.from && !value?.to) {
                    const daysDiff = Math.abs(differenceInDays(date, value.from));
                    return daysDiff > maxDays - 1;
                  }
                  return false;
                }}
              />
              {showTimeRanges && (
                <div className="time-filter-section">
                  <div className="time-filter-header">
                    <FiClock className="time-filter-icon" />
                    <span className="time-filter-title">Time Range</span>
                    {hasTimeFilter && (
                      <button className="clear-time-button" onClick={clearTimeFilter}>
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="time-inputs-row">
                    <TimeInput label="Start" value={timeFilter.startTime} onChange={(val) => handleTimeChange('startTime', val)} placeholder="00:00" />
                    <span className="time-separator">to</span>
                    <TimeInput label="End" value={timeFilter.endTime} onChange={(val) => handleTimeChange('endTime', val)} placeholder="23:59" />
                  </div>
                </div>
              )}
              {showClearButton && value && (
                <div className={'clear-button-container'}>
                  <button className={'clear-button'} onClick={onClear}>
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default DatePicker;
